import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useSiteConfigStore } from '../stores/siteConfigStore';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoading } = useAuthStore();
  const siteConfig = useSiteConfigStore((s) => s.config);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error('Vui lòng nhập đầy đủ thông tin');
      return;
    }

    const result = await login(formData.email, formData.password, rememberMe);

    if (result.success) {
      toast.success('Đăng nhập thành công!');
      // Admin/Director/Manager → Dashboard, others → Portal (personal info)
      const roleCode = useAuthStore.getState().user?.role?.role_code;
      const isManager = ['ADMIN', 'DIRECTOR', 'MANAGER'].includes(roleCode);
      navigate(isManager ? '/' : '/portal');
    } else {
      toast.error(result.message || 'Đăng nhập thất bại');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 px-4"
      style={siteConfig?.login_bg_color ? { background: `linear-gradient(135deg, ${siteConfig.primary_color || '#6366f1'}, ${siteConfig.login_bg_color})` } : {}}
    >
      <div className="max-w-md w-full">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          {/* Logo tròn — dùng /logo192.png (cache-bust để luôn lấy bản mới) */}
          <img
            src="/logo192.png?v=20260620"
            alt={siteConfig?.site_name || 'HR System'}
            className="w-20 h-20 rounded-full object-cover mb-4 shadow-lg ring-4 ring-white/40 mx-auto"
          />
          <h1 className="text-3xl font-bold text-white mb-2">{siteConfig?.site_name || 'HR Management System'}</h1>
          <p className="text-primary-100">{siteConfig?.site_description || 'Hệ thống quản lý nhân sự'}</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Đăng nhập</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="input"
                placeholder="your.email@company.com"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="label">
                Mật khẩu
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="input pr-10"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember"
                  name="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="remember" className="ml-2 block text-sm text-gray-700">
                  Ghi nhớ đăng nhập (30 ngày)
                </label>
              </div>
              <button
                type="button"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Quên mật khẩu?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Đang xử lý...
                </div>
              ) : (
                'Đăng nhập'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-primary-100">
          © {new Date().getFullYear()} {siteConfig?.site_name || 'HR Management System'}. All rights reserved.
        </p>
      </div>
    </div>
  );
}
